import { isHangingIndent } from "@vericus/cadmus-common/references";
import { SpellCheckSource } from "@vericus/cadmus-editor-prosemirror";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  AssessmentType,
  ExamTiming,
  TaskFormat,
  TaskKind,
} from "@/generated/graphql";

import {
  getExamReadingTime,
  getExamWritingTime,
  getTimeLimit,
  resolveSubmitAttemptLimit,
  updateAssessmentSettings,
  updateWorkTimeline,
} from "./assignment-actions";

export interface AssignmentState {
  /**
   * The type of assessment. (Assignment or Exam)
   */
  type: AssessmentType;

  /**
   * Assessment format is either classic or new multi-format.
   */
  format: TaskFormat;

  /**
   * The kind of task (Individual, Viva, etc.)
   */
  taskKind: TaskKind;

  /**
   * If the assessment is a group assessment.
   */
  groupAssessmentEnabled: boolean;

  /**
   * If the assessment has a viva (oral response) enabled. Drives response-time
   * wording in UI copy (see `responseTimeTerm`).
   */
  vivaEnabled: boolean;

  /**
   * If true, individual draft feedback is visible to students from the moment
   * it is produced, without waiting for `draftDueDate` to pass.
   */
  individualDraftFeedbackAlwaysAvailable: boolean;

  /**
   * If true, group draft feedback is visible to students from the moment it
   * is produced, without waiting for `groupDraftDueDate` to pass.
   */
  groupDraftFeedbackAlwaysAvailable: boolean;

  /**
   * Whether drafts are enabled for the group work component of a GA.
   */
  groupDraftEnabled: boolean;

  /**
   * Whether the student can resubmit after the due date.
   */
  allowLateResubmission: boolean;

  /**
   * The time limit for the assignment (in minutes).
   */
  timeLimit: number | null;

  /**
   * Whether the time limit has been extended.
   */
  timeLimitExtended: boolean;

  /**
   * The referencing style for the assignment.
   */
  referencingStyle: string | null;

  /**
   * Whether the style has hanging Indents.
   */
  hangingIndent: boolean;

  /**
   * Exam Setting
   *
   * If `autoSubmission` is not enabled, this is the time limit for late
   * submissions.
   * If `autoSubmission` is enabled and is set to null, the user can submit
   * late indefinitely.
   */
  lateSubmissionTimeLimit: number | null;

  /**
   * Exam Setting
   * Whether the work will be auto submitted at the end of the exam.
   */
  autoSubmission: boolean;

  /**
   * How many submission attempts the student is allow to make
   */
  submitAttemptLimit: number | null;

  /**
   * Exam Setting
   * The timing of the exam. Window or Live.
   */
  examTiming: ExamTiming;

  /**
   * Exam Setting
   * Whether disable backtrack navigation in Multi-Format exams.
   */
  examBacktrackDisabled: boolean | null;

  /**
   * Exam Setting
   * The reading time portion of the exam.
   * This is optional. If not set, the exam is considered to have
   * not been given reading time.
   */
  readingTime: number | null;

  /**
   * Exam Setting
   * The writing time portion of the exam.
   */
  writingTime: number | null;

  /**
   * Exam Setting
   * Exam is considered closed, where the current time is past the `examEndDate`.
   * No more submissions are accepted.
   */
  examIsOver: boolean;

  /**
   * Exam Setting
   * The student/enrollment been marked as deferred
   * exams. */
  examDeferred: boolean;

  /**
   * Exam Setting
   * Exam runs inside a lockdown browser.
   */
  isLockdownBrowser: boolean;

  /**
   * Anonymous marking setting for the assessment.
   */
  anonymousMarking: boolean;

  /**
   * Whether the student is permitted to use Generative AI.
   */
  allowGenerativeAi?: boolean | null;

  /**
   * Whether spell check is disabled for the assessment.
   *
   */
  spellCheckDisabled: boolean;

  /**
   * The student's chosen spell check source. This is a runtime preference set
   * via the settings menu, not a released teacher setting. When the teacher has
   * disabled spell check (`spellCheckDisabled`), this is ignored and the
   * effective source is always `null` (see `selectSpellCheckSource`).
   */
  spellCheckSource: SpellCheckSource;

  /**
   * Whether footnotes wordcount should be included in the word count.
   * This is only relevant for classic assessments.
   */
  countFootnotes: boolean;

  /**
   * Whether references wordcount should be included in the word count.
   * This is only relevant for classic assessments.
   */
  countReferences: boolean;
}

export const initialState: AssignmentState = {
  type: AssessmentType.Assignment,
  taskKind: TaskKind.Individual,
  format: TaskFormat.Classic,
  groupAssessmentEnabled: false,
  vivaEnabled: false,
  individualDraftFeedbackAlwaysAvailable: false,
  groupDraftFeedbackAlwaysAvailable: false,
  groupDraftEnabled: false,
  examTiming: ExamTiming.Live,
  examBacktrackDisabled: null,
  autoSubmission: false,
  submitAttemptLimit: null,
  allowLateResubmission: false,
  readingTime: null,
  writingTime: null,
  timeLimit: null,
  timeLimitExtended: false,
  lateSubmissionTimeLimit: null,
  referencingStyle: null,
  hangingIndent: false,
  examIsOver: false,
  examDeferred: false,
  isLockdownBrowser: false,
  anonymousMarking: false,
  spellCheckDisabled: false,
  spellCheckSource: "cadmus",
  allowGenerativeAi: null,
  countFootnotes: false,
  countReferences: false,
};

export const assignmentSlice = createSlice({
  name: "assignment",
  initialState,
  reducers: {
    markExamAsOver: (state) => {
      state.examIsOver = true;
    },
    onContinueExam: (state) => {
      state.examIsOver = false;
    },
    setTaskFormat: (state, action: PayloadAction<TaskFormat>) => {
      state.format = action.payload;
    },
    setTaskKind: (state, action: PayloadAction<TaskKind>) => {
      state.taskKind = action.payload;
    },
    setSpellCheckSource: (
      state,
      action: PayloadAction<SpellCheckSource>
    ) => {
      state.spellCheckSource = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateAssessmentSettings, (state, action) => {
      const settings = action.payload.settings;

      if (settings) {
        const isExam = settings.assessmentType === AssessmentType.Exam;

        state.type = settings.assessmentType;
        state.groupAssessmentEnabled = settings.groupAssessmentEnabled;
        state.vivaEnabled = settings.vivaEnabled;
        state.individualDraftFeedbackAlwaysAvailable =
          settings.individualDraftFeedbackAlwaysAvailable;
        state.groupDraftFeedbackAlwaysAvailable =
          settings.groupDraftFeedbackAlwaysAvailable;
        state.groupDraftEnabled = settings.groupDraftEnabled;
        state.examTiming = settings.examTiming;
        state.writingTime = settings.examWritingTime ?? null;
        state.readingTime =
          settings.examReadingTimeDisabled === false &&
          typeof settings.examReadingTime === "number"
            ? settings.examReadingTime
            : null;

        state.timeLimit =
          settings.timeLimitEnabled && typeof settings.timeLimit === "number"
            ? settings.timeLimit
            : null;

        state.referencingStyle = settings.referencingStyleEnabled
          ? settings.referencingStyle
          : null;
        state.hangingIndent =
          settings.referencingStyleEnabled && settings.referencingStyle
            ? isHangingIndent(settings.referencingStyle)
            : false;
        state.allowLateResubmission = settings.lateResubmissionEnabled;
        state.allowGenerativeAi = settings.allowGenerativeAi;
        state.anonymousMarking = settings.anonymousMarking ?? false;
        state.spellCheckDisabled = settings.spellCheckDisabled ?? false;
        if (state.spellCheckDisabled) {
          state.spellCheckSource = null;
        }
        state.countFootnotes = settings.countFootnotes ?? false;
        state.countReferences = settings.countReferences ?? false;

        if (isExam) {
          state.lateSubmissionTimeLimit = settings.lateSubmissionTimeLimit;
          state.autoSubmission = settings.examAutoSubmissionDisabled === false;

          state.isLockdownBrowser = settings.examLdbEnabled;
          state.examBacktrackDisabled = settings.examBacktrackDisabled;
        }
      }
    });

    builder.addCase(updateWorkTimeline, (state, action) => {
      const workSettings = action.payload.workSettings;

      const isExam = state.type === AssessmentType.Exam;
      const [readingTime] = getExamReadingTime(action.payload);
      const [writingTime] = getExamWritingTime(action.payload);
      state.readingTime = readingTime;
      state.writingTime = writingTime;

      const [timeLimit, timeLimitExtended] = getTimeLimit(action.payload);

      state.timeLimit = timeLimit;
      state.timeLimitExtended = timeLimitExtended;

      state.submitAttemptLimit = resolveSubmitAttemptLimit(action.payload);

      if (isExam) {
        state.examDeferred = workSettings?.examDeferred ?? false;
      }
    });
  },
});

export const {
  markExamAsOver,
  onContinueExam,
  setTaskFormat,
  setTaskKind,
  setSpellCheckSource,
} = assignmentSlice.actions;

export const assignmentReducer = assignmentSlice.reducer;
